import { gql, useMutation } from "@apollo/client";
import { useCallback } from "react";
import { MergeCartPayload, MutationMergeCartArgs } from "src/types/graphql.d";

type MergeCartData = {
  mergeCart: MergeCartPayload | null;
};

type UseMergeCartReturn = {
  /**
   * Hands a guest session over to the signed-in customer. `cartId` merges the
   * guest cart's line items into the customer's cart; `anonymousId` is recorded
   * on the customer so guest orders from that session can be linked later.
   * Resolves with the payload status (false on any error).
   */
  mergeCart: (variables: MutationMergeCartArgs) => Promise<boolean>;
  loading: boolean;
  error?: Error;
};

const MERGE_CART_MUTATION = gql`
  mutation mergeCart($anonymousId: String, $cartId: String) {
    mergeCart(anonymousId: $anonymousId, cartId: $cartId) {
      status
      message
    }
  }
`;

function useMergeCart(): UseMergeCartReturn {
  const [mergeCartMutation, { loading, error }] = useMutation<
    MergeCartData,
    MutationMergeCartArgs
  >(MERGE_CART_MUTATION, {
    refetchQueries: ["cart", "orders"],
  });

  const mergeCart = useCallback(
    async (variables: MutationMergeCartArgs): Promise<boolean> => {
      try {
        const result = await mergeCartMutation({ variables });

        return result.data?.mergeCart?.status === true;
      } catch (err) {
        console.error("Error merging cart:", err);

        return false;
      }
    },
    [mergeCartMutation]
  );

  return { mergeCart, loading, error };
}

export { useMergeCart, MERGE_CART_MUTATION };
